import { useCallback } from 'react';

import type { Screen } from '@/lib/kioskSession';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';

/**
 * Where “Back” goes — the menu they came from, or out of the flow.
 *
 * ⚠️ **The screens this serves have TWO entrances, and that is the whole problem.** `jobs` is reached from
 * the profile page by a new candidate AND from “What brings you in today?” by a returning one. `finish` is
 * reached from four places in the main flow and from the menu. `payCard` and `associateStatus` likewise.
 *
 * So neither fixed answer is right. Always ending the session strands the returning candidate: they pick
 * “Link pay card”, decide they meant something else, press Back and are thrown to Welcome —
 * their session gone, four factors to retype, for pressing Back once. Always returning to the menu is
 * worse in the other direction: a new candidate who never saw that menu lands on a screen asking why they
 * came in, halfway through applying.
 *
 * `arrival` is the signal, and it already exists: the store records the selection the moment an option is
 * tapped [KID-5], so a set value means *this person came through the menu* and nothing else does.
 *
 * It lives in one hook rather than in each screen because the per-screen version is what produced the bug
 * — five screens each answered the question separately, four got it wrong, and the one that got it right
 * was simply the one built last.
 *
 * `whenNotFromMenu` is for the screens whose main-flow predecessor is a real screen rather than the exit.
 * The pay card is the one: it sits AFTER a submitted application [KPC-1], so ending the session there would
 * discard somebody who has already applied — which is worse than the dead end this hook was written to fix.
 *
 * ⚠️ **Left unset, Back out of the flow ASKS — it does not end the session itself** [ISS-149]. It used to
 * call `endCandidateSession` here, so a candidate who opened a job, pressed Back to the list and pressed
 * Back once more was on Welcome with no question and their session gone (ISS UAT RK-058, reproduced on
 * dev). Leaving the visit always goes through the Exit confirmation [0199; 0340], and it is the SAME
 * overlay the top bar's `Exit` opens — `KioskScreen` draws it once. NO keeps them where they were; YES is
 * the end of the session KID-1 makes Back mean, with everything it clears.
 */
export function useBack(whenNotFromMenu?: Screen): () => void {
  const cameFromMenu = useKioskSession((state) => state.arrival !== undefined);
  const goTo = useKioskSession((state) => state.goTo);
  const askToExit = useKioskSession((state) => state.askToExit);

  return useCallback(() => {
    if (cameFromMenu) goTo(SCREEN.arrivalReason);
    else if (whenNotFromMenu !== undefined) goTo(whenNotFromMenu);
    else askToExit();
  }, [cameFromMenu, whenNotFromMenu, goTo, askToExit]);
}
